import { Ciclo, Dica, SecaoTitulo } from '../../shared/ui/primitivas';
import { TelaAnery } from '../../shared/ui/TelaAnery';
import type { AneryChip, AneryEtapa, AneryTone } from '../../shared/ui/tiposDaEsteira';

/**
 * O que o dia cobra agora — e nada além disso. Número do mês é a tela do
 * dashboard: quem abre isto às 8h precisa da janela do dia 10, não de
 * "312 pacientes ativos".
 *
 * Linha, não card. Um card por item daria aos nove o mesmo peso visual, e o
 * ponto da tela é que três param o faturamento e seis não.
 *
 * Fronteira da fonte: vira `useQuery` aqui quando houver o que ler.
 */
const VISAO_GERAL = {
  breadcrumb: 'operação / visão geral / bradesco · setembro-2026',
  titulo: 'Piloto Bradesco — o que o dia cobra',
  subtitulo:
    'Uma camada de trabalho sobre o IW. O IW segue fonte de verdade; aqui o trabalho ganha dono, prazo e histórico.',
  chips: [
    { label: '· 28,4% da carteira', strong: 'Bradesco', accent: true },
    { label: 'janelas internas ', strong: 'dia 3 · 10 · 23' },
    { label: 'humano em toda saída externa' },
  ] as AneryChip[],
  grupos: [
    {
      nome: 'Precisa de decisão humana',
      itens: [
        {
          titulo: 'Janela dia 10 · 2 dossiês incompletos',
          detalhe: 'falta Escala de Braden · o validador barra o envio',
          tom: 'block',
        },
        {
          titulo: '7 itens na fila de revisão',
          detalhe: 'CI ambígua · homônimo · a IA não teve certeza',
          tom: 'warn',
        },
        {
          titulo: 'Lote AD aguardando aprovação',
          detalhe: 'checklist verde, falta o clique · nenhum XML sai sem aprovação',
          tom: 'warn',
        },
      ],
    },
    {
      nome: 'Correndo sozinho',
      itens: [
        { titulo: 'CI de hoje classificada', detalhe: '38 de 41 sem revisão', tom: 'ok' },
        { titulo: 'Cobrança de relatório enviada', detalhe: '9 núcleos · com trilha', tom: 'ok' },
        { titulo: 'Dossiês montados em PDF único', detalhe: '31 para a janela dia 10', tom: 'ok' },
        {
          titulo: 'Planilhas de prestador normalizadas',
          detalhe: '12 prestadores · prazo dia 20',
          tom: 'ok',
        },
      ],
    },
    {
      nome: 'Bloqueado hoje',
      itens: [
        {
          titulo: 'Escrita no IW não existe',
          detalhe: 'só insert em HML · status e ocorrência ficam no painel e são replicados à mão',
          tom: 'neutral',
        },
        {
          titulo: 'Canal da Intuitive a descobrir',
          detalhe: 'export · API · portal? · trava a ingestão automática da glosa',
          tom: 'neutral',
        },
      ],
    },
  ] as { nome: string; itens: { titulo: string; detalhe: string; tom: AneryTone }[] }[],
  ciclo: [
    { nome: 'Captação', valor: '18', unidade: 'AD 24h · ID 48h · VM 72h', preenchimento: 22 },
    { nome: 'Conduta', valor: '31', unidade: 'roteada por núcleo', preenchimento: 38 },
    { nome: 'Prorrogação', valor: '94', unidade: 'dossiê vigiado', preenchimento: 64 },
    { nome: 'Faturamento', valor: '27', unidade: 'XML validado antes de sair', preenchimento: 47 },
    { nome: 'Glosas', valor: '41', unidade: 'prazo de recurso vigiado', preenchimento: 29 },
  ] as AneryEtapa[],
  dica: 'O número do mês — volume, SLA e o que a automação economizou — está no Dashboard executivo.',
};

/** O estado tem cor E palavra: cor sozinha não carrega informação. */
const ROTULO: Record<AneryTone, string> = {
  block: 'barrado',
  warn: 'aguardando',
  ok: 'em curso',
  info: 'info',
  neutral: 'travado',
};

const TOM: Record<AneryTone, string> = {
  block: 'barrado',
  warn: 'espera',
  ok: 'curso',
  info: 'info',
  neutral: 'neutral',
};

export function PaginaDaVisaoGeral(): React.JSX.Element {
  return (
    <TelaAnery
      breadcrumb={VISAO_GERAL.breadcrumb}
      titulo={VISAO_GERAL.titulo}
      subtitulo={VISAO_GERAL.subtitulo}
      chips={VISAO_GERAL.chips}
    >
      {VISAO_GERAL.grupos.map((g) => (
        <section key={g.nome}>
          <SecaoTitulo extra={String(g.itens.length)}>{g.nome}</SecaoTitulo>
          {g.itens.map((i) => (
            <div key={i.titulo} className="anery-item">
              <span className="anery-estado anery-estado-col cond" data-tom={TOM[i.tom]}>
                {ROTULO[i.tom]}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[0.9375rem] leading-snug">{i.titulo}</span>
                <span className="mt-0.5 block text-[0.8125rem] leading-snug text-[var(--tinta-2)]">
                  {i.detalhe}
                </span>
              </span>
            </div>
          ))}
        </section>
      ))}

      <SecaoTitulo extra="tickets abertos por etapa">Onde está o trabalho agora</SecaoTitulo>
      <div className="mt-3">
        <Ciclo etapas={VISAO_GERAL.ciclo} />
      </div>

      <Dica>{VISAO_GERAL.dica}</Dica>
    </TelaAnery>
  );
}
